import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { collection, getDocs } from 'firebase/firestore';
import { auth, db } from '../firebase';
import './AdminDashboard.css';

function AdminDashboard() {
  const navigate = useNavigate();
  const user = auth.currentUser;
  const [stats, setStats] = useState({
    totalProducts: 0,
    totalApplications: 0,
    pendingApplications: 0,
    totalFeedback: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      // Get products count
      const productsSnapshot = await getDocs(collection(db, 'products'));
      const totalProducts = productsSnapshot.size;

      // Get applications
      const appsSnapshot = await getDocs(collection(db, 'applications'));
      const totalApplications = appsSnapshot.size;
      let pendingCount = 0;
      appsSnapshot.forEach((doc) => {
        if (doc.data().status === 'pending') {
          pendingCount++;
        }
      });

      // Get feedback count
      const feedbackSnapshot = await getDocs(collection(db, 'feedback'));
      const totalFeedback = feedbackSnapshot.size;

      setStats({
        totalProducts,
        totalApplications,
        pendingApplications: pendingCount,
        totalFeedback
      });
      setLoading(false);
    } catch (error) {
      console.error('Error fetching stats:', error);
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  return (
    <div className="admin-dashboard">
      <nav className="admin-dashboard-nav">
        <div className="nav-container">
          <div className="logo">BetaBuyers Admin</div>
          <button onClick={handleLogout} className="btn-logout">Sign Out</button>
        </div>
      </nav>

      <div className="admin-dashboard-content">
        <div className="admin-dashboard-header">
          <div>
            <h1>Admin Dashboard</h1>
            <p className="welcome-text">Welcome back, {user?.email}</p>
          </div>
          <div className="header-actions">
            <span className="last-updated">Last updated: {new Date().toLocaleTimeString()}</span>
          </div>
        </div>

        {/* Stats Overview */}
        <div className="stats-section">
          <h2 className="section-title">Overview</h2>
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-header">
                <span className="stat-label">Total Products</span>
              </div>
              <div className="stat-value">{loading ? '...' : stats.totalProducts}</div>
              <div className="stat-footer">Active products in system</div>
            </div>

            <div className="stat-card">
              <div className="stat-header">
                <span className="stat-label">Total Applications</span>
              </div>
              <div className="stat-value">{loading ? '...' : stats.totalApplications}</div>
              <div className="stat-footer">All-time submissions</div>
            </div>

            <div className="stat-card highlight">
              <div className="stat-header">
                <span className="stat-label">Pending Reviews</span>
              </div>
              <div className="stat-value">{loading ? '...' : stats.pendingApplications}</div>
              <div className="stat-footer">Requires your attention</div>
            </div>

            <div className="stat-card">
              <div className="stat-header">
                <span className="stat-label">Feedback Received</span>
              </div>
              <div className="stat-value">{loading ? '...' : stats.totalFeedback}</div>
              <div className="stat-footer">Total feedback submissions</div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="actions-section">
          <h2 className="section-title">Management</h2>
          <div className="admin-actions-grid">
            <div className="admin-action-card">
              <div className="action-header">
                <div className="action-icon-box blue">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                    <line x1="3" y1="9" x2="21" y2="9"></line>
                    <line x1="9" y1="21" x2="9" y2="9"></line>
                  </svg>
                </div>
                <h3>Product Management</h3>
              </div>
              <p>Create, edit, and manage beta testing products available to users</p>
              <button onClick={() => navigate('/products')} className="btn-action">
                Manage Products
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="9 18 15 12 9 6"></polyline>
                </svg>
              </button>
            </div>

            <div className="admin-action-card">
              <div className="action-header">
                <div className="action-icon-box green">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                    <polyline points="14 2 14 8 20 8"></polyline>
                    <line x1="12" y1="18" x2="12" y2="12"></line>
                    <line x1="9" y1="15" x2="15" y2="15"></line>
                  </svg>
                </div>
                <h3>Application Review</h3>
              </div>
              <p>Review and approve or reject beta tester applications from users</p>
              <button onClick={() => navigate('/admin')} className="btn-action">
                Review Applications
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="9 18 15 12 9 6"></polyline>
                </svg>
              </button>
            </div>

            <div className="admin-action-card">
              <div className="action-header">
                <div className="action-icon-box purple">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                  </svg>
                </div>
                <h3>Feedback Management</h3>
              </div>
              <p>View and analyze feedback submissions from approved beta testers</p>
              <button onClick={() => navigate('/admin-feedback')} className="btn-action">
                View Feedback
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="9 18 15 12 9 6"></polyline>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminDashboard;